// As duas únicas funções da política comercial que tocam banco — separadas de
// politicaComercial.ts para aquele arquivo poder ficar 100% puro e testável
// com node:test (ver o comentário no topo de lá). Se uma função nova de
// política comercial precisar de supabase, ela entra aqui, não lá.
import { supabase } from './supabase.ts';
import {
  ModalidadePlano,
  MODALIDADES_VALIDAS,
  PerfilParaModalidade,
  modalidadePorPerfil,
  calcularMultiplicadorFinal,
  MultiplicadorResultado,
} from './politicaComercial.ts';

/** Versão async para telas que só têm o id da negociação (ex.: UnidadeForm).
 *  Sem negociação vinculada (UC avulsa) cai no cargo/departamento de quem está criando. */
export async function carregarModalidadePlano(
  negociacaoId: number | null | undefined,
  viewer?: PerfilParaModalidade | null
): Promise<ModalidadePlano> {
  if (!negociacaoId) return modalidadePorPerfil(viewer);

  const { data: neg } = await supabase
    .from('negociacoes')
    .select('plano_modalidade, responsavel_id')
    .eq('id', negociacaoId)
    .single();
  if (!neg) return modalidadePorPerfil(viewer);

  if (MODALIDADES_VALIDAS.includes(neg.plano_modalidade as ModalidadePlano)) {
    return neg.plano_modalidade as ModalidadePlano;
  }
  if (!neg.responsavel_id) return 'vendedor';

  const { data: resp } = await supabase
    .from('profiles')
    .select('role, departamento, departamentos')
    .eq('id', neg.responsavel_id)
    .single();
  return modalidadePorPerfil(resp as PerfilParaModalidade | null);
}

/**
 * Re-soma o consumo das UCs da negociação, reclassifica o público e recalcula
 * o multiplicador final, persistindo em negociacoes. Retorna null se a
 * negociação não tem plano definido; caso contrário informa se houve mudança.
 *
 * Só vale para o vendedor externo (plano_codigo) — Comercial Interno não tem
 * público nem plano_codigo, então não passa por aqui (ver comissaoInternaV1.ts).
 */
export async function recalcularCondicaoComercial(
  negociacaoId: number,
  opcoes: {
    /**
     * Calcula e devolve o efeito SEM gravar nada.
     *
     * Existe porque o aviso de "público reclassificado, multiplicador mudou"
     * chegava depois do fato consumado: o usuário salvava a UC e só então
     * descobria que tinha mexido na comissão, sem chance de desistir. Com a
     * simulação a tela pergunta antes.
     */
    simular?: boolean;
  } = {},
): Promise<
  | null
  | { mudou: boolean; volumeTotal: number; anterior: { publico: string | null; multiplicadorFinal: number | null }; atual: MultiplicadorResultado }
> {
  const { data: neg, error: negError } = await supabase
    .from('negociacoes')
    .select('id, fidelidade, plano_codigo, publico, multiplicador_final')
    .eq('id', negociacaoId)
    .single();
  if (negError || !neg || !neg.plano_codigo) return null;

  const { data: ucs, error: ucsError } = await supabase
    .from('unidades_consumidoras')
    .select('consumo_medio_kwh')
    .eq('negociacao_id', negociacaoId);
  if (ucsError) return null;

  const volumeTotal = (ucs || []).reduce((s, u) => s + (Number(u.consumo_medio_kwh) || 0), 0);
  const atual = calcularMultiplicadorFinal(neg.plano_codigo, volumeTotal, neg.fidelidade);
  const mudou = atual.publico !== neg.publico || atual.final !== Number(neg.multiplicador_final);

  if (mudou && !opcoes.simular) {
    await supabase
      .from('negociacoes')
      .update({
        consumo_medio_kwh: volumeTotal,
        publico: atual.publico,
        multiplicador_plano: atual.multPlano,
        fator_fidelidade: atual.fatorFidelidade,
        multiplicador_final: atual.final,
      })
      .eq('id', negociacaoId);
  }

  return {
    mudou,
    volumeTotal,
    anterior: { publico: neg.publico, multiplicadorFinal: neg.multiplicador_final !== null ? Number(neg.multiplicador_final) : null },
    atual,
  };
}
